import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Flex,
  Icon,
  useColorModeValue,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Divider
} from '@chakra-ui/react';
import { FiTrendingUp, FiAlertCircle, FiDollarSign, FiActivity, FiRefreshCw, FiServer, FiCheckCircle } from 'react-icons/fi';

// PA demo: fully self-contained — no backend calls.
// Scale: large PA FQHC (~46,000 patients, 6 sites) => ~14,250 claims / 30 days.
const PA_ANALYTICS = {
  metrics: {
    total_claims: 14250,
    dollars_at_risk: 187430.00,
    dollars_recovered: 412680.00
  },
  denial_root_causes: [
    { cause: 'BH carve-out misroute (billed to PH-MCO instead of BH-MCO)', count: 412, amount: 76632.00 },
    { cause: 'Duplicate same-type encounter (two medical T1015, same DOS)', count: 187, amount: 34782.00 },
    { cause: 'COB / TPL — commercial primary not billed first', count: 141, amount: 26226.00 },
    { cause: 'Missed Medicare Advantage wrap (Rev 0519 not submitted)', count: 138, amount: 26041.00 },
    { cause: 'Medical necessity — diagnosis specificity (CARC 50)', count: 96, amount: 17856.00 },
    { cause: 'Lacks information / billing error (CARC 16)', count: 31, amount: 5893.00 }
  ]
};

const FqPaDashboard = () => {
  const { selectedClient } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    // Simulate a short load so the UI behaves like the live version.
    setIsLoading(true);
    const t = setTimeout(() => {
      setAnalytics(JSON.parse(JSON.stringify(PA_ANALYTICS)));
      setIsLoading(false);
    }, 400);
    return () => clearTimeout(t);
  }, [selectedClient]);

  const handleSyncClearinghouse = async () => {
    setIsSyncing(true);
    // Simulate network delay for dramatic effect
    setTimeout(() => {
      setIsSyncing(false);
      onOpen(); // Open the batch processing modal
    }, 1500);
  };

  const handleGenerateWraparounds = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      onClose();
      toast({
        title: "Batch Wrap Claims Submitted",
        description: "Successfully generated and submitted 138 Medicare Advantage supplemental wrap claims (Rev 0519) to Novitas (JL).",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Update local state to show the impact
      setAnalytics(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          dollars_recovered: prev.metrics.dollars_recovered + 64500,
          dollars_at_risk: prev.metrics.dollars_at_risk - 64500
        }
      }));
    }, 2000);
  };

  if (isLoading || !analytics) {
    return <Box p={8}><Text>Loading analytics...</Text></Box>;
  }

  const { metrics, denial_root_causes } = analytics;

  return (
    <Box maxW="7xl" mx="auto">
      <Flex justify="space-between" align="flex-start" mb={8}>
        <Box>
          <Heading size="lg" mb={2}>
            {selectedClient === 'all' ? 'Command Center: Aggregate View' : 'Client Dashboard'}
          </Heading>
          <Text color="gray.500">
            {selectedClient === 'all' 
              ? 'Multi-tenant pipeline monitoring across all EMR integrations.' 
              : 'Isolated view for the selected FQHC client.'}
          </Text>
        </Box>
        <Button 
          leftIcon={<FiRefreshCw />} 
          colorScheme="blue" 
          variant="solid"
          isLoading={isSyncing}
          loadingText="Ingesting 835s..."
          onClick={handleSyncClearinghouse}
          size="lg"
          shadow="md"
        >
          Sync Clearinghouse (Batch)
        </Button>
      </Flex>

      {/* Top Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
          <CardBody>
            <Stat>
              <Flex align="center" mb={2}>
                <Icon as={FiActivity} color="blue.500" mr={2} />
                <StatLabel>Total Claims Ingested</StatLabel>
              </Flex>
              <StatNumber>{metrics.total_claims.toLocaleString()}</StatNumber>
              <StatHelpText>Last 30 days</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
          <CardBody>
            <Stat>
              <Flex align="center" mb={2}>
                <Icon as={FiCheckCircle} color="green.500" mr={2} />
                <StatLabel>First Pass Resolution Rate</StatLabel>
              </Flex>
              <StatNumber color="green.500">96.4%</StatNumber>
              <StatHelpText>+2.1% since Revedy implementation</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
          <CardBody>
            <Stat>
              <Flex align="center" mb={2}>
                <Icon as={FiAlertCircle} color="red.500" mr={2} />
                <StatLabel>Dollars At Risk</StatLabel>
              </Flex>
              <StatNumber color="red.500">
                ${metrics.dollars_at_risk.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </StatNumber>
              <StatHelpText>Pending manual review</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
          <CardBody>
            <Stat>
              <Flex align="center" mb={2}>
                <Icon as={FiDollarSign} color="green.500" mr={2} />
                <StatLabel>Dollars Recovered</StatLabel>
              </Flex>
              <StatNumber color="green.500">
                ${metrics.dollars_recovered.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </StatNumber>
              <StatHelpText>Via automated wraparounds & appeals</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Multi-EMR Volume Breakdown */}
      {selectedClient === 'all' && (
        <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm" mb={8}>
          <CardHeader pb={0}>
            <Flex align="center">
              <Icon as={FiServer} mr={2} color="gray.500" />
              <Heading size="md">Live Pipeline Volume by EMR</Heading>
            </Flex>
            <Text color="gray.500" fontSize="sm" mt={1}>
              Normalized 837P ingestion across all client systems.
            </Text>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold">OCHIN Epic</Text>
                  <Badge colorScheme="green">Active</Badge>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold">5,000</Text>
                <Text fontSize="sm" color="gray.500">Claims Ingested</Text>
                <Progress value={98} colorScheme="green" size="sm" mt={3} mb={1} />
                <Text fontSize="xs" color="gray.500" textAlign="right">98% FPRR</Text>
              </Box>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold">eClinicalWorks</Text>
                  <Badge colorScheme="green">Active</Badge>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold">3,200</Text>
                <Text fontSize="sm" color="gray.500">Claims Ingested</Text>
                <Progress value={95} colorScheme="green" size="sm" mt={3} mb={1} />
                <Text fontSize="xs" color="gray.500" textAlign="right">95% FPRR</Text>
              </Box>
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold">NextGen</Text>
                  <Badge colorScheme="green">Active</Badge>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold">6,050</Text>
                <Text fontSize="sm" color="gray.500">Claims Ingested</Text>
                <Progress value={97} colorScheme="green" size="sm" mt={3} mb={1} />
                <Text fontSize="xs" color="gray.500" textAlign="right">97% FPRR</Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Denial Root Causes */}
      <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
        <CardHeader pb={0}>
          <Heading size="md">Denial Root Causes (FQHC Specific)</Heading>
          <Text color="gray.500" fontSize="sm" mt={1}>
            Categorized by CARC/RARC codes and Revedy rules engine analysis.
          </Text>
        </CardHeader>
        <CardBody>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Root Cause</Th>
                <Th isNumeric>Claim Count</Th>
                <Th isNumeric>Amount At Risk</Th>
                <Th w="30%">Impact</Th>
              </Tr>
            </Thead>
            <Tbody>
              {denial_root_causes.map((cause, index) => {
                // Calculate percentage for the progress bar based on the highest amount
                const maxAmount = Math.max(...denial_root_causes.map(c => c.amount));
                const percent = (cause.amount / maxAmount) * 100;
                
                return (
                  <Tr key={index}>
                    <Td fontWeight="medium">
                      <Button 
                        variant="link" 
                        colorScheme="blue" 
                        onClick={() => {
                          // Navigate to the claim detail view with a mock 835 denial
                          const mock835Data = {
                            type: '835',
                            patient: { first_name: "Maria", last_name: "Reyes" },
                            provider: { name: "David T. Nguyen, MD" },
                            denial_reason: cause.cause,
                            rules_fired: [
                              {
                                rule_name: "Denial Root Cause Analysis",
                                description: `Clearinghouse returned CARC code mapping to: ${cause.cause}`,
                                action: "Draft Appeal Letter",
                                citation: "PA FQHC/RHC PROMISe Provider Handbook & OMAP MA Bulletins"
                              }
                            ]
                          };
                          // We use window.location to navigate since we don't have useNavigate imported here
                          window.location.href = '/fq-pa/claim/demo';
                          // In a real app we'd use navigate, but for the demo we can just set sessionStorage
                          sessionStorage.setItem('mockClaimData', JSON.stringify(mock835Data));
                        }}
                      >
                        {cause.cause}
                      </Button>
                    </Td>
                    <Td isNumeric>{cause.count}</Td>
                    <Td isNumeric>${cause.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Td>
                    <Td>
                      <Progress value={percent} colorScheme="red" size="sm" borderRadius="full" />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Batch Processing Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>Clearinghouse Sync Complete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100">
                <HStack mb={2}>
                  <Icon as={FiCheckCircle} color="blue.500" />
                  <Text fontWeight="bold" color="blue.800">Batch Processing Summary</Text>
                </HStack>
                <Text fontSize="sm" color="blue.700">
                  Successfully ingested and parsed overnight 835 remittance files from Waystar and TriZetto.
                </Text>
              </Box>
              
              <SimpleGrid columns={2} spacing={4}>
                <Box p={3} borderWidth="1px" borderRadius="md">
                  <Text fontSize="sm" color="gray.500">Remittances Processed</Text>
                  <Text fontSize="xl" fontWeight="bold">8,400</Text>
                </Box>
                <Box p={3} borderWidth="1px" borderRadius="md">
                  <Text fontSize="sm" color="gray.500">Underpayments Found</Text>
                  <Text fontSize="xl" fontWeight="bold" color="orange.500">138</Text>
                </Box>
              </SimpleGrid>

              <Divider />

              <Box>
                <Text fontWeight="bold" mb={1}>Medicare Advantage Wrap Opportunity Identified</Text>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Revedy compared the Medicare FQHC PPS rate against Medicare Advantage plan payments for dual-eligible encounters. 138 claims qualify for the supplemental wrap payment (Rev 0519) from Novitas. Note: PA HealthChoices MCOs are required to pay at least the Medicaid PPS rate directly, so no Medicaid-side wrap gap exists.
                </Text>
                <HStack justify="space-between" p={3} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                  <Text fontWeight="bold" color="green.800">Total Wrap Owed (Medicare):</Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.600">$64,500.00</Text>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter bg="gray.50" borderTopWidth="1px" mt={4}>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Review Manually
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleGenerateWraparounds}
              isLoading={isGenerating}
              loadingText="Generating Claims..."
            >
              Auto-Generate & Submit 138 Wrap Claims
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default FqPaDashboard;
